import { useState } from "react";
import { observer } from "mobx-react-lite";
import { useUploadQueueStore } from "@/hooks/useUploadQueueStore";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronUp, RefreshCw, AlertCircle } from "lucide-react";

/**
 * FailedUploadsPanel - Expandable panel showing failed uploads with retry options.
 *
 * Features:
 * - Collapsible list of failed files
 * - Error message for each failed file
 * - "Retry All" button to retry all failed uploads
 * - Individual file error details
 *
 * Only renders when there are failed uploads.
 */
export const FailedUploadsPanel = observer(function FailedUploadsPanel() {
  const uploadStore = useUploadQueueStore();
  const [isExpanded, setIsExpanded] = useState(false);

  const failedFiles = uploadStore.failedFiles;
  const hasRetryableFiles = failedFiles.some((f) => f.retryable !== false);

  // Don't show if no failures
  if (failedFiles.length === 0) {
    return null;
  }

  const handleRetryAll = () => {
    uploadStore.retryFailed();
    uploadStore.startProcessing();
  };

  return (
    <Card className="border-destructive/50">
      <CardHeader className="px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <CardTitle className="text-sm font-medium">
              {failedFiles.length} Failed Upload{failedFiles.length !== 1 ? "s" : ""}
            </CardTitle>
          </div>

          <div className="flex items-center gap-2">
            {hasRetryableFiles && (
              <Button variant="outline" size="sm" onClick={handleRetryAll} className="h-7 text-xs">
                <RefreshCw className="mr-1 h-3 w-3" />
                Retry All
              </Button>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="h-7 w-7 p-0"
            >
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="px-4 pb-3 pt-0">
          <div className="max-h-48 space-y-2 overflow-y-auto">
            {failedFiles.map((file) => (
              <div
                key={file.id}
                className={cn(
                  "flex items-start justify-between gap-2 rounded-md p-2",
                  "bg-muted/50 text-sm"
                )}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium" title={file.fileName}>
                    {file.fileName}
                  </p>
                  {file.error && (
                    <p className="mt-0.5 text-xs text-muted-foreground">{file.error}</p>
                  )}
                </div>

                <Badge variant="destructive" className="shrink-0 text-xs">
                  Failed
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
});
